import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Clock, LogOut, LayoutDashboard, Users, FileText, ClipboardList, Palette, Clock3, Sparkles, Tablet, Building2, GraduationCap, Wallet } from 'lucide-react';
import DashboardTab from '../components/admin/DashboardTab';
import PersonasTab from '../components/admin/PersonasTab';
import ReportesTab from '../components/admin/ReportesTab';
import FichajesTab from '../components/admin/FichajesTab';
import TurnosTab from '../components/admin/TurnosTab';
import CentrosSection from '../components/admin/CentrosSection';
import AjustesTab from '../components/admin/AjustesTab';
import FormacionTab from '../components/admin/FormacionTab';
import FacturacionTab from '../components/admin/FacturacionTab';
import ImpersonationBanner from '../components/admin/ImpersonationBanner';
import DesviacionBanner from '../components/admin/DesviacionBanner';
import BloqueoPagoScreen from '../components/admin/BloqueoPagoScreen';

const AdminApp = () => {
  const { user, logout, puede } = useAuth();
  const navigate = useNavigate();
  const color = user?.color || '#18181B';
  const logo = user?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${user.logo_url}` : null;

  const salir = () => { logout(); navigate('/'); };

  // Empresa bloqueada por impago tras prueba + gracia
  if (user?.bloqueada) {
    return <BloqueoPagoScreen />;
  }

  // Módulos visibles según permisos. Sub-agrupaciones:
  //   - "Empleados" contiene también Comunicados (PersonasTab)
  //   - "Centros" contiene también Calendario laboral (CentrosSection)
  const tabs = [
    { value: 'dashboard', label: 'Resumen', icon: LayoutDashboard, modulo: 'resumen', Comp: DashboardTab },
    { value: 'centros', label: 'Centros', icon: Building2, modulo: 'centros', Comp: CentrosSection },
    { value: 'empleados', label: 'Empleados', icon: Users, modulo: 'empleados', Comp: PersonasTab },
    { value: 'turnos', label: 'Turnos', icon: Clock3, modulo: 'turnos', Comp: TurnosTab },
    { value: 'fichajes', label: 'Fichajes', icon: ClipboardList, modulo: 'fichajes', Comp: FichajesTab },
    { value: 'reportes', label: 'Informes', icon: FileText, modulo: 'informes', Comp: ReportesTab },
    { value: 'formacion', label: 'Formación', icon: GraduationCap, modulo: 'formacion', Comp: FormacionTab },
    { value: 'facturacion', label: 'Facturación', icon: Wallet, modulo: 'facturacion', Comp: FacturacionTab },
    { value: 'ajustes', label: 'Ajustes', icon: Palette, modulo: 'ajustes', Comp: AjustesTab },
  ].filter((t) => puede(t.modulo));

  const defaultTab = tabs[0]?.value || 'dashboard';

  return (
    <div className="min-h-screen bg-zinc-50">
      <ImpersonationBanner />
      <DesviacionBanner />
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            {logo ? (
              <img src={logo} alt="logo" className="h-10 w-10 rounded-xl object-contain bg-white border border-zinc-200" />
            ) : (
              <div className="p-2 rounded-xl" style={{ backgroundColor: color }}><Clock className="h-5 w-5 text-white" /></div>
            )}
            <div>
              <h1 className="font-heading text-lg font-bold text-zinc-900">
                {user?.empresa_nombre || 'Control Horario'}
              </h1>
              <p className="text-zinc-500 text-xs">{user?.full_name} · {user?.role === 'admin' ? 'Administrador' : (user?.role || 'Colaborador')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild data-testid="admin-kiosk-btn">
              <Link to="/kiosko"><Tablet className="h-4 w-4 mr-2" /> Kiosko</Link>
            </Button>
            <Button variant="outline" onClick={salir} data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4 mr-2" /> Salir
            </Button>
          </div>
        </div>
      </header>

      {user?.estado_cuenta === 'prueba' && user?.dias_prueba_restantes != null && (
        <div
          data-testid="trial-banner"
          className={`text-sm ${user.dias_prueba_restantes <= 5 ? 'bg-red-600' : 'bg-zinc-900'} text-white`}
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-2 flex-wrap justify-center">
            <Sparkles className="h-4 w-4" />
            {user.dias_prueba_restantes >= 0
              ? <span>Estás en <b>periodo de prueba</b> · Te quedan <b>{user.dias_prueba_restantes} días</b>.</span>
              : <span>Tu periodo de prueba ha caducado.</span>}
            <button
              onClick={() => document.querySelector('[data-testid="tab-facturacion"]')?.click()}
              data-testid="trial-elegir-plan"
              className="ml-2 rounded-md bg-white/15 hover:bg-white/25 px-3 py-1 font-semibold underline underline-offset-2">
              Elegir un plan
            </button>
          </div>
        </div>
      )}

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue={defaultTab} className="space-y-6">
          {/*
            Layout robusto para cualquier número de pestañas:
              - En pantallas anchas: fila horizontal ajustada al contenido
              - En medianas/estrechas: scroll horizontal sin cortar contenido
              - NO usa clases dinámicas (que Tailwind purga en producción)
          */}
          <div className="w-full overflow-x-auto -mx-1 px-1 pb-1">
            <TabsList className="inline-flex h-auto w-max min-w-full items-center gap-1 bg-zinc-100 p-1 rounded-lg">
              {tabs.map((t) => {
                const Icon = t.icon;
                return (
                  <TabsTrigger key={t.value} value={t.value} data-testid={`tab-${t.value}`}
                    className="flex-shrink-0 whitespace-nowrap px-3 py-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">
                    <Icon className="h-4 w-4 mr-2" /> {t.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>
          {tabs.map((t) => {
            const Comp = t.Comp;
            return <TabsContent key={t.value} value={t.value}><Comp /></TabsContent>;
          })}
        </Tabs>
      </main>
    </div>
  );
};

export default AdminApp;
